import { View, Dialog } from 'web-mojo';

/**
 * NotesPanel — reusable notes thread for any object.
 *
 * Usage:
 *   new NotesPanel({ filter: { machine: 17 } })
 *   new NotesPanel({ filter: { location: 5 } })
 *   new NotesPanel({ filter: { template_stop: 22 } })
 */
export default class NotesPanel extends View {
    constructor({ filter = {} } = {}) {
        super();
        this.filter = filter;
        this.notes = [];
        this.loading = false;
    }

    async onInit() {
        await this.loadNotes();
    }

    async loadNotes() {
        this.loading = true;
        this.render();

        const response = await app.api.get('/api/camaccess/note', {
            ...this.filter,
            sort: '-created_at',
            size: 100,
        });

        this.loading = false;
        if (response.success && response.data.status) {
            this.notes = response.data.data || [];
        }
        this.render();
    }

    async onActionAddNote() {
        const body = this.element.querySelector('[name=note_body]')?.value.trim();
        if (!body) return;

        const isPinned = this.element.querySelector('[name=is_pinned]')?.checked || false;

        const response = await app.api.post('/api/camaccess/note', {
            body,
            is_pinned: isPinned,
            ...this.filter,
        });

        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to add note');
            return;
        }

        this.element.querySelector('[name=note_body]').value = '';
        this.element.querySelector('[name=is_pinned]').checked = false;
        await this.loadNotes();
    }

    async onActionTogglePin(event, el) {
        const noteId = el.getAttribute('data-note-id');
        const note = this.notes.find(n => String(n.id) === noteId);
        if (!note) return;

        const response = await app.api.patch(`/api/camaccess/note/${noteId}`, {
            is_pinned: !note.is_pinned,
        });
        if (response.success && response.data.status) {
            await this.loadNotes();
        }
    }

    async onActionDeleteNote(event, el) {
        const noteId = el.getAttribute('data-note-id');
        const confirmed = await Dialog.confirm({
            title: 'Delete Note',
            message: 'Delete this note? This cannot be undone.',
            confirmText: 'Delete',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;

        const response = await app.api.delete(`/api/camaccess/note/${noteId}`);
        if (response.success) {
            await this.loadNotes();
        }
    }

    _noteHtml(note) {
        const author = note.author?.display_name || note.author?.username || 'Unknown';
        const time = note.created_at
            ? new Date(note.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
            : '';
        const pinIcon = note.is_pinned
            ? '<i class="bi bi-pin-angle-fill text-warning"></i>'
            : '<i class="bi bi-pin-angle text-muted"></i>';
        const flaggedBadge = note.flagged
            ? '<span class="badge bg-danger ms-1">Flagged</span>'
            : '';
        const pinnedBadge = note.is_pinned
            ? '<span class="badge bg-warning text-dark ms-1">Pinned</span>'
            : '';

        return `
            <div class="d-flex gap-2 py-2 px-3 border-bottom ${note.is_pinned ? 'bg-warning bg-opacity-10' : ''}">
                <div class="flex-grow-1">
                    <div class="small text-muted mb-1">
                        <span class="fw-semibold text-body">${author}</span>
                        <span class="ms-2">${time}</span>
                        ${pinnedBadge}${flaggedBadge}
                    </div>
                    <div class="small">${note.body}</div>
                </div>
                <div class="d-flex flex-column gap-1 flex-shrink-0">
                    <button type="button" class="btn btn-link btn-sm p-0" title="${note.is_pinned ? 'Unpin' : 'Pin'}"
                        data-action="toggle-pin" data-note-id="${note.id}">
                        ${pinIcon}
                    </button>
                    <button type="button" class="btn btn-link btn-sm p-0 text-danger" title="Delete"
                        data-action="delete-note" data-note-id="${note.id}">
                        <i class="bi bi-trash3"></i>
                    </button>
                </div>
            </div>
        `;
    }

    get template() {
        if (this.loading) {
            return `<div class="text-center py-4 text-muted"><div class="spinner-border spinner-border-sm me-2"></div>Loading…</div>`;
        }

        const pinned = this.notes.filter(n => n.is_pinned);
        const unpinned = this.notes.filter(n => !n.is_pinned);
        const ordered = [...pinned, ...unpinned];

        const notesList = ordered.length > 0
            ? `<div class="border rounded mb-3">${ordered.map(n => this._noteHtml(n)).join('')}</div>`
            : `<div class="text-muted small text-center py-3 mb-3">No notes yet.</div>`;

        return `
            ${notesList}
            <div class="border rounded p-3 bg-light">
                <textarea class="form-control form-control-sm mb-2" name="note_body" rows="2"
                    placeholder="Add a note…"></textarea>
                <div class="d-flex justify-content-between align-items-center">
                    <div class="form-check form-check-inline mb-0">
                        <input class="form-check-input" type="checkbox" name="is_pinned" id="pin_note_${this._uid()}">
                        <label class="form-check-label small" for="pin_note_${this._uid()}">Pin note</label>
                    </div>
                    <button type="button" class="btn btn-sm btn-primary" data-action="add-note">
                        <i class="bi bi-plus-lg me-1"></i>Add Note
                    </button>
                </div>
            </div>
        `;
    }

    set template(v) { this._t = v; }

    _uid() {
        return this._id || (this._id = Math.random().toString(36).slice(2));
    }
}
